import {AttackType, ComputedSetStats, JobData, JobDataConst, LevelStats, ScalingOverrides} from "./geartypes";
import {chanceMultiplierStdDev, fixedValue, multiplyValues, ValueWithDev} from "./deviation";

/*
    Common math for FFXIV.

    These mostly come from AkhMorning, however, there is one important difference - where possible, these are normalized
    to 1. That is, if something has no impact on the final result, it should be 1, rather than 100 or 1000. If it
    grants +50% damage, then it should be 1.5, rather than 150 or 1500, and so on.

    Most of these do not need to be used directly, because the values are already calculated on the ComputedSetStats
    object. It is preferable to use already-computed values since those are also exposed on the UI. This ensures
    consistency with the displayed values.
 */
/**
 * Enhanced flooring function which takes into account a small margin of error to account for
 * floating point errors.
 *
 * e.g. 2.3 * 100 => 229.99999999999997, which would normally floor to 229, but fl(2.3 * 100) => 230
 *
 * @param input
 */
export function fl(input: number) {
    const floored = Math.floor(input);
    const loss = input - floored;
    // e.g. if input is 2.999..., then floored === 2 and loss === 0.999...
    // so we can just return floor + 1;
    if (loss >= 0.99999995) {
        return floored + 1;
    }
    else {
        return floored;
    }
}

/**
 * Enhanced ceiling function which takes into account a small margin of error to account for
 * floating point errors.
 *
 * e.g. 100 * 1.1 => 110.00000000000001, which would normally ceil to 111, but cl(100 * 1.1) => 110
 *
 * @param input
 */
export function cl(input: number) {
    const ceiled = Math.ceil(input);
    const loss = ceiled - input;
    // e.g. if input is 110.00000000000001, then ceiled === 111 and loss === 0.999...
    // so we can just return ceiled - 1;
    if (loss >= 0.99999995) {
        return ceiled - 1;
    }
    else {
        return ceiled;
    }
}

/**
 * Truncate to an integer value (i.e. round towards zero).
 *
 * @param input
 */
export function trunc(input: number): number {
    if (input > 0) {
        return fl(input);
    }
    else if (input < 0) {
        return -fl(-input);
    }
    else {
        return 0;
    }
}

/**
 * Floor a number to the given precision.
 *
 * e.g. flp(2, 1.239) => 1.23
 *
 * @param places The number of decimal places to keep. Must be a non-negative integer. Zero is allowed,
 * but you should just call 'fl' directly in that case.
 * @param input The number to round.
 */
export function flp(places: number, input: number) {
    if (places % 1 !== 0 || places < 0) {
        throw Error(`Invalid places input ${places} - must be non-negative integer`);
    }
    const multiplier = Math.pow(10, places);
    return fl(input * multiplier) / multiplier;
}

/**
 * Ceil a number to the given precision.
 *
 * e.g. flp(2, 1.231) => 1.24
 *
 * @param places The number of decimal places to keep. Must be a non-negative integer. Zero is allowed,
 * but you should just call 'cl' directly in that case.
 * @param input The number to round.
 */
export function clp(places: number, input: number) {
    if (places % 1 !== 0 || places < 0) {
        throw Error(`Invalid places input ${places} - must be non-negative integer`);
    }
    const multiplier = Math.pow(10, places);
    return cl(input * multiplier) / multiplier;
}

/**
 * Convert skill speed to GCD speed.
 *
 * @param baseGcd The base time of the ability in question (e.g. 2.5 for most abilities).
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param sks The skill speed stat value.
 * @param haste The haste value, e.g. 15 for 15% haste, etc.
 */
export function sksToGcd(baseGcd: number, levelStats: LevelStats, sks: number, haste = 0): number {
    return Math.max(0, fl((fl((1000 - fl(130 * (sks - levelStats.baseSubStat) / levelStats.levelDiv)) * baseGcd) * (100 - haste)) / 1000) / 100);
}

/**
 * Convert spell speed to GCD speed.
 *
 * @param baseGcd The base time of the ability in question (e.g. 2.5 for most abilities).
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param sps The spell speed stat value.
 * @param haste The haste value, e.g. 15 for 15% haste, etc.
 */
export function spsToGcd(baseGcd: number, levelStats: LevelStats, sps: number, haste = 0): number {
    return Math.max(0, fl((fl((1000 - fl(130 * (sps - levelStats.baseSubStat) / levelStats.levelDiv)) * baseGcd) * (100 - haste)) / 1000) / 100);
}

/**
 * Convert crit stat to crit chance.
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param crit The critical hit stat value.
 */
export function critChance(levelStats: LevelStats, crit: number) {
    return fl(200 * (crit - levelStats.baseSubStat) / levelStats.levelDiv + 50) / 1000.0;
}

/**
 * Convert crit stat to crit damage multiplier.
 *
 * AkhMorning equivalent: F(CRIT)
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param crit The critical hit stat value.
 */
export function critDmg(levelStats: LevelStats, crit: number) {
    return (1400 + fl(200 * (crit - levelStats.baseSubStat) / levelStats.levelDiv)) / 1000.0;
}

/**
 * Convert direct hit stat to direct hit chance.
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param dhit The direct hit stat value.
 */
export function dhitChance(levelStats: LevelStats, dhit: number) {
    return fl(550 * (dhit - levelStats.baseSubStat) / levelStats.levelDiv) / 1000.0;
}

/**
 * Convert direct hit stat to direct hit multiplier. Note that this is a fixed multiplier at the moment. This
 * function is only here in case that changes in the future.
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param dhit The direct hit stat value.
 */
export function dhitDmg(levelStats: LevelStats, dhit: number) {
    return 1.25;
}

/**
 * Convert a determination stat to its respective damage multiplier.
 *
 * AkhMorning equivalent: F(DET)
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param det The determination stat value.
 */
export function detDmg(levelStats: LevelStats, det: number) {
    return (1000 + fl(140 * (det - levelStats.baseMainStat) / levelStats.levelDiv)) / 1000.0;
}

/**
 * Convert a Weapon Damage value to a damage multiplier.
 *
 * AkhMorning equivalent: F(WD)
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param jobStats Job stats for the job for which the computation is to be performed.
 * @param wd The weapon damage value.
 * @param petAction Whether this is a pet action.
 */
export function wdMulti(levelStats: LevelStats, jobStats: JobData, wd: number, petAction: boolean = false) {
    // TODO: better way to handle this?
    if (jobStats.type !== 'Combat') {
        return 1;
    }
    let mainStatJobMod = jobStats.jobStatMultipliers[jobStats.mainStat];
    if (petAction) {
        // All pet actions use a job mod of 100.
        mainStatJobMod = 100;
    }
    return fl(levelStats.baseMainStat * mainStatJobMod / 1000 + wd) / 100;
}

/**
 * Convert a spell speed amount to a DoT/HoT tick multiplier.
 *
 * AkhMorning equivalent: F(SPD)
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param sps The spell speed.
 */
export function spsTickMulti(levelStats: LevelStats, sps: number) {
    return (1000 + fl(130 * (sps - levelStats.baseSubStat) / levelStats.levelDiv)) / 1000;
}

/**
 * Convert a skill speed amount to a DoT/HoT tick multiplier.
 *
 * AkhMorning equivalent: F(SPD)
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param sks The skill speed.
 */
export function sksTickMulti(levelStats: LevelStats, sks: number) {
    return (1000 + fl(130 * (sks - levelStats.baseSubStat) / levelStats.levelDiv)) / 1000;
}

/**
 * Convert a main stat value to a damage multiplier.
 *
 * AkhMorning equivalent: F(AP) or F(ATK)
 *
 * @param levelStats
 * @param jobStats
 * @param mainstat
 */
export function mainStatMulti(levelStats: LevelStats, jobStats: JobData, mainstat: number) {
    const apMod = mainStatPowerMod(levelStats, jobStats);
    return Math.max(0, (trunc(apMod * (mainstat - levelStats.baseMainStat) / levelStats.baseMainStat) + 100) / 100);
}

/**
 * Convert a main stat value to a damage multiplier for Living Shadow abilities.
 *
 * @param levelStats
 * @param livingShadowStrength
 */
export function mainStatMultiLivingShadow(levelStats: LevelStats, livingShadowStrength: number) {
    // Living Shadow always uses the 'other' power scaling, i.e.
    // without Tank Mastery.
    const apMod = levelStats.mainStatPowerMod['other'];
    return Math.max(0, (trunc(apMod * (livingShadowStrength - levelStats.baseMainStat) / levelStats.baseMainStat) + 100) / 100);
}

/**
 * Convert a tenacity stat value to its respective damage multiplier.
 *
 * AkhMorning equivalent: F(TNC)
 *
 * @param levelStats
 * @param tenacity
 */
export function tenacityDmg(levelStats: LevelStats, tenacity: number) {
    return (1000 + fl(112 * (tenacity - levelStats.baseSubStat) / levelStats.levelDiv)) / 1000;
}

/**
 * Convert defense (or magic defense) to a damage taken ratio (i.e. 0.95 means 5% mitigation).
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param defense The defense (or magic defense) value.
 */
export function defIncomingDmg(levelStats: LevelStats, defense: number) {
    return Math.max(0, (100 - fl(15 * defense / levelStats.levelDiv)) / 100);
}

/**
 * Convert a tenacity stat value to its respective incoming damage reduction. This is represented as a multiplier,
 * i.e. a return of 0.95 represents a 5% mitigation.
 *
 * @param levelStats
 * @param tenacity
 */
export function tenacityIncomingDmg(levelStats: LevelStats, tenacity: number) {
    return (1000 - fl(200 * (tenacity - levelStats.baseSubStat) / levelStats.levelDiv)) / 1000;
}

/**
 * Convert direct hit stat to a damage multiplier for auto-DH abilities. Having more direct hit will provide more
 * damage in cases where the ability automatically direct hits.
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param dhit The direct hit stat value.
 */
export function autoDhitBonusDmg(levelStats: LevelStats, dhit: number) {
    return fl(140 * ((dhit - levelStats.baseSubStat) / levelStats.levelDiv)) / 1000;
}

/**
 * Compute auto-crit bonus damage granted by crit chance buffs which would otherwise be redundant on an auto-crit.
 *
 * @param critMulti       The normal critical strike multiplier.
 * @param bonusCritChance The amount of extra critical strike chance from buffs.
 */
export function autoCritBuffDmg(critMulti: number, bonusCritChance: number) {
    return flp(3, 1 + ((critMulti - 1) * bonusCritChance));
}

/**
 * Compute auto-dhit bonus damage granted by dhit chance buffs which would otherwise be redundant on an auto-dhit.
 *
 * @param dhMulti       The normal critical strike multiplier.
 * @param bonusDhChance The amount of extra critical strike chance from buffs.
 */
export function autoDhitBuffDmg(dhMulti: number, bonusDhChance: number) {
    return flp(3, 1 + ((dhMulti - 1) * bonusDhChance));
}

/**
 * Convert a piety stat to (MP regen)/tick amount.
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param piety The piety stat.
 */
export function mpTick(levelStats: LevelStats, piety: number) {
    return 200 + fl(150 * (piety - levelStats.baseMainStat) / levelStats.levelDiv);
}

/**
 * Like wdMulti, but for auto-attacks.
 *
 * AkhMorning equivalent: f(AUTO)
 *
 * @param levelStats level stats
 * @param jobStats job stats
 * @param weaponDelay weapon delay in seconds
 * @param weaponDamage weapon damage
 */
export function autoAttackModifier(levelStats: LevelStats, jobStats: JobData, weaponDelay: number, weaponDamage: number) {
    // TODO: better way to handle this?
    if (jobStats.type !== 'Combat') {
        return 1;
    }
    return fl(fl(levelStats.baseMainStat * jobStats.jobStatMultipliers[jobStats.autoAttackStat] / 1000 + weaponDamage) * (weaponDelay / 3)) / 100;
}

/**
 * Computes base damage. Does not factor in crit/dh RNG nor damage variance.
 */
export function baseDamage(...args: Parameters<typeof baseDamageFull>): number {
    return baseDamageFull(...args).expected;
}

/**
 * Determines whether the caster variant of the damage formula should be used instead of the normal variant.
 *
 * @param stats The set stats.
 * @param attackType The type of attack.
 */
function usesCasterDamageFormula(stats: ComputedSetStats, attackType: AttackType): boolean {
    return (stats.jobStats.combatRole === 'Caster' || stats.jobStats.combatRole === 'Healer')
        && attackType !== 'Auto-attack';
}

/**
 * Gets Living Shadow's strength value from a given set of gear stats and racial bonuses.
 *
 * @param rawStrength The raw strength (pre party bonus)
 * @param baseMainStat The base main stat for this level
 * @param playerBaseMainStat The player's base main stat, i.e. main stat for this level + job mod + racial bonus
 */
export function getLivingShadowStrength(rawStrength: number, baseMainStat: number, playerBaseMainStat: number): number {
    const livingShadowRacialBonus = 2;
    return rawStrength - playerBaseMainStat + baseMainStat + livingShadowRacialBonus;
}

/**
 * Gets Automaton Queen's dex value from a given set of gear stats and racial bonuses.
 *
 * @param rawDex The raw dex (pre party bonus)
 * @param baseMainStat The base main stat for this level
 * @param playerBaseMainStat The player's base main stat, i.e. main stat for this level + job mod + racial bonus
 */
export function getAutomatonQueenDex(rawDex: number, baseMainStat: number, playerBaseMainStat: number): number {
    const automatonQueenRacialBonus = 0;
    return rawDex - playerBaseMainStat + baseMainStat + automatonQueenRacialBonus;
}

/**
 * Returns the "zero" ScalingOverrides object, which represents normal scalings for
 * an ability.
 */
export function getDefaultScalings(stats: ComputedSetStats): ScalingOverrides {
    return {
        mainStatMulti: stats.mainStatMulti,
        wdMulti: stats.wdMulti,
        addSkillSpeedMultiplier: false,
    };
}

/**
 * Computes base damage. Does not factor in crit/dh RNG nor damage variance.
 */
export function baseDamageFull(stats: ComputedSetStats, potency: number, attackType: AttackType = 'Unknown', autoDH: boolean = false, isDot: boolean = false, scalingOverrides = getDefaultScalings(stats), autoCrit: boolean = false): ValueWithDev {
    let spdMulti: number;
    const isAA = attackType === 'Auto-attack';
    if (isAA) {
        spdMulti = stats.sksDotMulti;
    }
    else if (isDot) {
        // SkS: "Affects both the casting and recast timers, as well as the damage over time potency for
        // weaponskills and auto-attacks. The higher the value, the shorter the timers/higher the potency."
        // SpS: "Affects both the casting and recast timers for spells. The higher the value, the shorter
        // the timers. Also affects a spell's damage over time or healing over time potency."
        spdMulti = (attackType === 'Weaponskill') ? stats.sksDotMulti : stats.spsDotMulti;
    }
    else if (scalingOverrides.addSkillSpeedMultiplier) {
        spdMulti = stats.sksDotMulti;
    }
    else {
        spdMulti = 1.0;
    }

    // Multiplier from main stat
    let mainStatMulti = scalingOverrides.mainStatMulti;

    // Multiplier from weapon damage.
    let wdMulti = scalingOverrides.wdMulti;

    //If this is an auto-attack, use the AA multis.
    if (isAA) {
        mainStatMulti = stats.aaStatMulti;
        wdMulti = stats.aaMulti;
    }

    // Det multiplier
    const detMulti = stats.detMulti;
    // Extra damage from auto DH bonus
    const autoDhBonus = stats.autoDhBonus;
    const tncMulti = stats.tncMulti; // if tank you'd do Funcs.fTEN(stats.tenacity, level) / 1000
    const detAutoDhMulti = flp(3, detMulti + autoDhBonus);
    const traitMulti = stats.traitMulti(attackType);
    const effectiveDetMulti = autoDH ? detAutoDhMulti : detMulti;

    // Base action potency and main stat multi
    let afterSpeed: number;
    // Mahdi:
    // Caster Damage has potency multiplied into weapon damage and then truncated
    // to an integer as opposed to into ap and truncated to 2 decimal.
    if (usesCasterDamageFormula(stats, attackType)) {
        // https://github.com/Amarantine-xiv/Amas-FF14-Combat-Sim_source/blob/main/ama_xiv_combat_sim/simulator/calcs/compute_damage_utils.py#L130
        const apDet = flp(2, mainStatMulti * effectiveDetMulti);
        const basePotency = fl(apDet * fl(wdMulti * potency));
        // Factor in Tenacity multiplier
        const afterTnc = fl(basePotency * tncMulti);
        // Factor in sps/sks for dots
        afterSpeed = fl(afterTnc * spdMulti);
    }
    else {
        const basePotency = fl(potency * mainStatMulti);
        // Factor in determination and auto DH multiplier
        const afterDet = fl(basePotency * effectiveDetMulti);
        // Factor in Tenacity multiplier
        const afterTnc = fl(afterDet * tncMulti);
        // Factor in weapon damage multiplier
        const afterWeaponDamage = fl(afterTnc * wdMulti);
        // Factor in sps/sks for dots
        afterSpeed = fl(afterWeaponDamage * spdMulti);
    }

    // Factor in trait multiplier, as well as the 1 extra damage if potency is less than 100
    const finalDamage = fl(afterSpeed * traitMulti) + ((potency < 100) ? 1 : 0);

    if (finalDamage <= 1) {
        return fixedValue(1);
    }
    else {
        // +-5% damage variance, uniform distribution.
        // Full formula is sqrt((max - min)^2 / 12)
        // === sqrt((1.05d - 0.95d)^2 / 12)
        // === sqrt((.1d)^2 / 12)
        // === sqrt(d^2 * .01 / 12)
        // === d * sqrt(.01 / 12)
        const stdDev = Math.sqrt(0.01 / 12) * finalDamage;
        return {
            expected: finalDamage,
            stdDev: stdDev,
        };
    }
}

export function baseHealing(stats: ComputedSetStats, potency: number, attackType: AttackType = 'Unknown', autoCrit: boolean = false) {

    // Multiplier from main stat
    const mainStatMulti = stats.mainStatMulti;
    // Multiplier from weapon damage
    const wdMulti = stats.wdMulti;
    // Multiplier for a successful crit
    const critMulti = stats.critMulti;
    // Crit chance
    // const critRate = gear.computedStats.critChance + extraBuffCritRate;
    const critRate = stats.critChance;
    // Dh chance
    // Det multiplier
    const detMulti = stats.detMulti;
    // Extra damage from auto DH bonus
    const tncMulti = stats.tncMulti;
    const traitMulti = stats.traitMulti(attackType);

    // Base action potency and main stat multi
    const basePotency = fl(potency * mainStatMulti * 100) / 100;
    // Factor in determination and auto DH multiplier
    const afterDet = fl(basePotency * detMulti * 100) / 100;
    // Factor in Tenacity multiplier
    const afterTnc = fl(afterDet * tncMulti * 100) / 100;
    // Factor in weapon damage multiplier
    const afterWeaponDamage = fl(afterTnc * wdMulti);
    // const d5 = fl(fl(afterWeaponDamage * critMulti) * DH_MULT)
    // Factor in auto crit multiplier
    const afterAutoCrit = autoCrit ? fl(afterWeaponDamage * (1 + (critRate * (critMulti - 1)))) : afterWeaponDamage;
    // Factor in auto DH multiplier
    // Factor in trait multiplier
    const afterTrait = fl(fl(afterAutoCrit * traitMulti) / 100);
    // console.log([basePotency, afterDet, afterTnc, afterWeaponDamage, d5, afterAutoCrit, d7, afterTrait]);

    if (afterTrait <= 0) {
        return 1;
    }
    else {
        return afterTrait;
    }
}

/**
 * Factors in the expected multiplier value resulting from random direct hits. Used for general damage.
 *
 * @param baseDamage The base damage amount.
 * @param stats The stats.
 * @deprecated Use {@link applyDhCritFull}
 */
export function applyDhCrit(baseDamage: number, stats: ComputedSetStats) {
    return baseDamage * (1 + stats.dhitChance * (stats.dhitMulti - 1)) * (1 + stats.critChance * (stats.critMulti - 1));
}

export function dhCritPercentStdDev(stats: ComputedSetStats, forcedCrit: boolean, forcedDhit: boolean) {
    return multiplyValues(
        forcedCrit ? chanceMultiplierStdDev(1, stats.critMulti * stats.autoCritBuffMulti) : chanceMultiplierStdDev(stats.critChance, stats.critMulti),
        forcedDhit ? chanceMultiplierStdDev(1, stats.dhitMulti * stats.autoDhitBuffMulti) : chanceMultiplierStdDev(stats.dhitChance, stats.dhitMulti)
    );
}

export function applyDhCritFull(baseDamage: ValueWithDev, stats: ComputedSetStats, forcedCrit: boolean, forcedDh: boolean): ValueWithDev {
    const stdDevFromCritDh = dhCritPercentStdDev(stats, forcedCrit, forcedDh);
    return multiplyValues(baseDamage, stdDevFromCritDh);
}

/**
 * Factors in the expected multiplier value resulting from random critical hits. Used for healing, as healing can crit
 * but not direct hit.
 *
 * @param baseDamage The base damage amount.
 * @param stats The stats.
 */
export function applyCrit(baseDamage: number, stats: ComputedSetStats) {
    return baseDamage * (1 + stats.critChance * (stats.critMulti - 1));
}

/**
 * Convert vitality to hp.
 *
 * @param levelStats Level stats for the level at which the computation is to be performed.
 * @param jobStats Job stats.
 * @param vitality The vitality stat.
 */
export function vitToHp(levelStats: LevelStats, jobStats: JobData, vitality: number) {
    // TODO make this work without ts-ignore
    const hpMod = hpScalar(levelStats, jobStats);
    return fl(levelStats.hp * jobStats.jobStatMultipliers.hp / 100) + fl((vitality - levelStats.baseMainStat) * hpMod);
}

export function hpScalar(levelStats: LevelStats, jobStats: JobDataConst) {
    // @ts-expect-error - can't figure out type def
    return levelStats.hpScalar[jobStats.combatRole] ?? levelStats.hpScalar.other;
}

export function mainStatPowerMod(levelStats: LevelStats, jobStats: JobDataConst) {
    // TODO: better way to handle this?
    if (jobStats.type !== 'Combat') {
        return 1;
    }
    return levelStats.mainStatPowerMod[jobStats.combatRole] ?? levelStats.mainStatPowerMod.other;
}

/**
 * Given a total buff haste, total gear haste, and total trait haste, compute the combined haste amount.
 *
 * @param buffHaste Haste from buffs, as a percentage.
 * @param gearHaste Haste from gear, as a percentage.
 * @param traitHaste Haste from traits, as a percentage.
 * @param gaugeHaste Haste from certain job-specific mechanics such as BRD's Paeon.
 */
export function combineHasteTypes(buffHaste: number, gearHaste: number, traitHaste: number, gaugeHaste: number): number {
    // Normalize each haste amount to a speed multiplier i.e. 1 = no haste, lower values = faster.
    const buffHasteMult = flp(2, (100 - buffHaste) / 100);
    // Specific to Paeon.
    // We don't actually know the order of operations for trait haste + gauge haste, but it isn't currently possible
    // to have both.
    const gaugeHasteMult = flp(2, (100 - gaugeHaste) / 100);
    const gearHasteMult = flp(2, (100 - gearHaste) / 100);
    const traitHasteMult = flp(2, (100 - traitHaste) / 100);
    // const combined = flp(2, flp(2, buffHasteMult * gearHasteMult) * traitHasteMult);
    // const combined = flp(2, flp(2, buffHasteMult * traitHasteMult) * gearHasteMult);
    const combined = flp(2, clp(2, flp(2, buffHasteMult * traitHasteMult) * gaugeHasteMult) * gearHasteMult);
    // Convert back to a haste percentage.
    return fl((1 - combined) * 100);
}

/**
 * Given a running total of haste buffs so far, combine the next buff's haste with the existing haste and return
 * the result. The result can then be plugged back into this function for the next buff, and so on.
 *
 * @param existingHaste
 * @param nextBuffHaste
 */
export function combineHasteBuffs(existingHaste: number, nextBuffHaste: number) {
    // incoming haste value is expressed as a percentage, so we need to convert it to a modifier.
    const existingMult = flp(2, (100 - existingHaste) / 100);
    const nextMult = flp(2, (100 - nextBuffHaste) / 100);
    const combinedMult = flp(2, existingMult * nextMult);
    return fl(100 * (1 - combinedMult));
}

/**
 * Calculate the stat cap for a given meldParam, ilvlModifier, and baseParamModifierSlotModifier
 *
 * @param meldParam The appropriate BaseParam.MeldParam value. Index comes from Item.BaseParamModifier. i.e.
 *               this is BaseParam.MeldParam[Item.BaseParamModifier].
 * @param ilvlModifier From ItemLevel[BaseParam.Name].
 * @param baseParamModifierSlotModifier From BaseParam[slot].
 */
export function statCapWithJob(meldParam: number, ilvlModifier: number, baseParamModifierSlotModifier: number): number {
    return Math.round(fl(fl(ilvlModifier * meldParam / 100) * baseParamModifierSlotModifier / 100) / 10);
}

/**
 * Calculate the stat cap for stats which do not have a jobCap available.
 *
 * @param ilvlModifier
 * @param baseParamSlotModifier
 */
export function statCapNoJob(ilvlModifier: number, baseParamSlotModifier: number): number {
    return Math.round(fl(ilvlModifier * baseParamSlotModifier / 100) / 10);
}

